// API-specific types for requests and responses
// Shared across frontend, mobile, and backend
//
// Schemas are authoritative; the TS types are derived via z.infer<>. Consumers
// can import the type names (unchanged from the pre-migration API) for
// compile-time typing, or the matching *Schema exports for runtime validation
// of API responses, form input, or Firestore reads.

import { z } from 'zod';

/**
 * Ad placement types
 */
export const AdPlacementSchema = z.enum(['quiz_interstitial', 'feed_banner', 'feed_inline']);
export type AdPlacement = z.infer<typeof AdPlacementSchema>;

/**
 * Ad response structure from backend /ads/serve endpoint
 */
export const AdResponseSchema = z.object({
  id: z.string(),
  /** FK to the ad's campaign — served for click/impression attribution. */
  campaignId: z.string(),
  imageUrl: z.string().optional(),
  text: z.string().optional(),
  ctaText: z.string().optional(),
  targetUrl: z.string().optional(),
  /** Where the ad renders. Same value the management `Ad` shape calls `placement`. */
  type: AdPlacementSchema,
  directBuy: z.boolean().optional(),
  /**
   * Proof-of-serve token: an HMAC-signed claim, minted at serve time, that the client returns on
   * POST /ads/impression so the server can bill only impressions for ads it actually served (see
   * services/adProofToken). Present only when proof-of-serve is configured; opaque to the client.
   */
  token: z.string().optional(),
});
export type AdResponse = z.infer<typeof AdResponseSchema>;

/**
 * Full ad serve response (wraps multiple ads + config)
 */
export const AdServeResponseSchema = z.object({
  ads: z.array(AdResponseSchema),
  frequency: z.number(),
  enabled: z.boolean(),
});
export type AdServeResponse = z.infer<typeof AdServeResponseSchema>;

/**
 * Request structure for serving ads
 */
export const AdServeRequestSchema = z.object({
  placement: AdPlacementSchema,
});
export type AdServeRequest = z.infer<typeof AdServeRequestSchema>;
